'use client';

import { IconPresentation } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { BlogSidebarItem } from './blog-sidebar-item';
import { DemoPopup } from '../DemoPopup';

interface MarketingDemoProps {
	title?: string;
	description?: string;
	className?: string;
	stickyTopOffset?: string;
}

export const MarketingDemo = ({
	title = 'Try Our Platform',
	description = 'Schedule a personalized demo to see how our platform can help your business grow.',
	className,
	stickyTopOffset = '1rem',
}: MarketingDemoProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [dimensions, setDimensions] = useState<{
		width?: number;
		left?: number;
	}>({});

	useEffect(() => {
		if (containerRef.current) {
			// Calculate initial dimensions
			updateDimensions();

			// Update dimensions on resize
			const handleResize = () => updateDimensions();
			window.addEventListener('resize', handleResize);

			// Update dimensions on scroll (in case parent elements change position)
			const handleScroll = () => updateDimensions();
			window.addEventListener('scroll', handleScroll, { passive: true });

			return () => {
				window.removeEventListener('resize', handleResize);
				window.removeEventListener('scroll', handleScroll);
			};
		}
	}, []);

	const updateDimensions = () => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			setDimensions({
				width: rect.width,
				left: rect.left,
			});
		}
	};

	// Create exact copy for the placeholder to ensure same height
	const demoContent = (isPlaceholder = false) => (
		<BlogSidebarItem
			icon={<IconPresentation size={18} />}
			title={title}
			contentClassName="bg-gradient-to-br from-primary/5 to-primary/10 p-6 relative overflow-hidden">
			{!isPlaceholder && (
				<>
					{/* Decorative elements */}
					<div className="absolute -right-6 -top-6 h-16 w-16 rounded-full bg-primary/10"></div>
					<div className="absolute -bottom-8 -left-8 h-20 w-20 rounded-full bg-primary/5"></div>
				</>
			)}

			<div className="relative flex flex-col items-center space-y-4">
				<p className="text-center text-sm leading-relaxed text-neutral-700">
					{description}
				</p>
				<div className="w-full max-w-[220px] pt-2">
					{isPlaceholder ? (
						<div className="h-10 w-full"></div>
					) : (
						<DemoPopup
							variant="default"
							buttonText="Schedule a Demo"
							className={cn(
								'w-full justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:translate-y-[-1px] hover:bg-primary/90 hover:shadow-md',
							)}
						/>
					)}
				</div>
			</div>
		</BlogSidebarItem>
	);

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			{/* Placeholder with the same height as the sticky content */}
			{/* <div className="invisible">{demoContent(true)}</div> */}

			{/* Sticky content */}
			<div
				className="sticky z-10"
				style={{
					top: stickyTopOffset,
					width: dimensions.width ? `${dimensions.width}px` : '100%',
					left: dimensions.left ? `${dimensions.left}px` : 'auto',
				}}>
				{demoContent()}
			</div>
		</div>
	);
};
